'use client';

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { useSession } from 'next-auth/react';

const focusSx = {
  '&:focus-visible, &:focus': {
    outline: '3px solid',
    outlineColor: 'rgba(25,118,210,0.9)',
    outlineOffset: '2px',
  },
};

type ReferralStats = {
  total: number;
  referred: { id: string; emailMasked: string; createdAt: string; plan: string }[];
  referralCredits?: number;
} | null;

/**
 * Single source of truth for the Referral Program block on profile pages.
 * Used by both (authenticated)/dashboard/profile and dashboard/profile.
 */
export function ReferralProgramSection() {
  const { data: sessionData, update } = useSession();
  const [username, setUsername] = React.useState(sessionData?.user?.username ?? '');
  const [isEditingUsername, setIsEditingUsername] = React.useState(false);
  const [usernameError, setUsernameError] = React.useState('');
  const [referralCopySnack, setReferralCopySnack] = React.useState(false);
  const [referralCopyError, setReferralCopyError] = React.useState(false);
  const [referralStats, setReferralStats] = React.useState<ReferralStats>(null);

  const referralBaseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://floai.studio';
  const referralIdentifier = sessionData?.user?.username || sessionData?.user?.id;
  const referralLink = referralIdentifier ? `${referralBaseUrl}/refer/${referralIdentifier}` : '';

  React.useEffect(() => {
    setUsername(sessionData?.user?.username ?? '');
  }, [sessionData?.user?.username]);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/referrals/stats', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setReferralStats(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleUsernameSave = React.useCallback(async () => {
    setUsernameError('');
    const raw = username.trim().toLowerCase();
    if (!raw) {
      setUsernameError('Enter a username.');
      return;
    }
    try {
      const res = await fetch('/api/user/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: raw }),
      });
      const data = await res.json();
      if (!res.ok) {
        setUsernameError(data?.error || 'Failed to save username.');
        return;
      }
      await update({ username: data.username ?? raw });
      setIsEditingUsername(false);
    } catch (e) {
      setUsernameError('Failed to save username.');
    }
  }, [username, update]);

  const handleUsernameCancel = React.useCallback(() => {
    setUsername(sessionData?.user?.username ?? '');
    setUsernameError('');
    setIsEditingUsername(false);
  }, [sessionData?.user?.username]);

  const handleCopyReferralLink = React.useCallback(() => {
    if (!referralLink) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(referralLink)
        .then(() => setReferralCopySnack(true))
        .catch(() => setReferralCopyError(true));
    } else {
      setReferralCopyError(true);
    }
  }, [referralLink]);

  const handleShareReferralEmail = React.useCallback(() => {
    if (!referralLink) return;
    const subject = encodeURIComponent('Join me on FloAI');
    const body = encodeURIComponent(`I thought you might like FloAI. Sign up here: ${referralLink}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }, [referralLink]);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Referral Program
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Earn 1 credit when a friend signs up with your link and subscribes to a paid plan. Your username is used in your link—set it below if you haven&apos;t.
      </Typography>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          {isEditingUsername ? (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                label="Username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setUsernameError('');
                }}
                error={!!usernameError}
                helperText={usernameError}
                placeholder="e.g. jane-doe"
                sx={{ maxWidth: 220 }}
              />
              <Button size="small" variant="contained" onClick={handleUsernameSave}>
                Save
              </Button>
              <Button size="small" variant="outlined" onClick={handleUsernameCancel}>
                Cancel
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              <Typography variant="body1">
                <strong>Username:</strong> {sessionData?.user?.username || 'Not set'}
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setIsEditingUsername(true)} sx={focusSx}>
                Edit
              </Button>
            </Box>
          )}
          <TextField
            fullWidth
            label="Your Referral Link"
            variant="outlined"
            value={referralLink}
            placeholder={referralIdentifier ? undefined : 'Set your username above to get your referral link.'}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
          <Button
            type="button"
            variant="contained"
            color="primary"
            sx={{ mr: 1, ...focusSx }}
            onClick={handleCopyReferralLink}
            disabled={!referralLink}
          >
            Copy Link
          </Button>
          <Button type="button" variant="outlined" sx={focusSx} onClick={handleShareReferralEmail} disabled={!referralLink}>
            Share via Email
          </Button>
          <Snackbar
            open={referralCopySnack}
            autoHideDuration={4000}
            onClose={() => setReferralCopySnack(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity="success" onClose={() => setReferralCopySnack(false)}>
              Link copied to clipboard.
            </Alert>
          </Snackbar>
          <Snackbar
            open={referralCopyError}
            autoHideDuration={5000}
            onClose={() => setReferralCopyError(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity="warning" onClose={() => setReferralCopyError(false)}>
              Copy failed. Please select and copy the link manually.
            </Alert>
          </Snackbar>
        </CardContent>
      </Card>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {referralStats ? (
          <>
            You have referred <strong>{referralStats.total}</strong> {referralStats.total === 1 ? 'person' : 'people'}. Referral credits:{' '}
            <strong>{referralStats.referralCredits ?? 0}</strong>
          </>
        ) : (
          'Loading referral stats…'
        )}
      </Typography>
      {referralStats?.referred?.length ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Referred users
          </Typography>
          <List dense disablePadding>
            {referralStats.referred.slice(0, 10).map((u) => (
              <ListItem key={u.id} disablePadding sx={{ py: 0.25 }}>
                <ListItemText primary={u.emailMasked} secondary={new Date(u.createdAt).toLocaleDateString()} />
                <Chip size="small" label={u.plan} sx={{ ml: 1 }} />
              </ListItem>
            ))}
            {referralStats.referred.length > 10 && (
              <ListItem disablePadding>
                <ListItemText secondary={`+ ${referralStats.referred.length - 10} more`} />
              </ListItem>
            )}
          </List>
        </Box>
      ) : null}
      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
        Terms: 1 credit per referred friend who subscribes; no self-referral. We may revoke credits for abuse.
      </Typography>
    </Box>
  );
}
